import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { ArrowLeft, Eye, EyeOff, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Label from '../../components/ui/Label';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import OrgRolesPicker, { orgIdsMissingRoles } from '../../components/OrgRolesPicker';
import UserAccessTags from '../../components/UserAccessTags';
import { useAuth } from '../../contexts/AuthContext';
import { getRoleCatalog } from '../../services/rolesService';
import { organizationsService } from '../../services/organizationsService';
import { EMAIL_RE, adminUsersApi, apiErrorText, fullName, sanitizeEmail, sanitizeName, sanitizeTitle } from './adminUsersApi';

function FlagToggle({ label, description, checked, onChange, disabled, disabledReason }) {
  return (
    <label className={`flex items-start gap-3 rounded-md border border-hairline p-3 ${disabled ? 'opacity-60' : ''}`} title={disabled ? disabledReason : undefined}>
      <input type="checkbox" className="mt-0.5" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <span>
        <span className="block font-medium text-[#18181B]">{label}</span>
        <span className="block text-xs text-zinc-500">{description}</span>
      </span>
    </label>
  );
}

/**
 * Platform-admin user detail page (`/admin/users/:id`): password, profile, access flags, and
 * organizations with per-org roles. New users are added from the list page's "+" dialog.
 */
function AdminUserDetail() {
  const { id } = useParams();
  const { user: me } = useAuth();

  const [catalog, setCatalog] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [profile, setProfile] = useState({ first_name: '', last_name: '', email: '', title: '' });
  const [profileErrors, setProfileErrors] = useState({});
  const [savingProfile, setSavingProfile] = useState(false);

  const [orgIds, setOrgIds] = useState([]);
  const [orgRoles, setOrgRoles] = useState({});
  const [savingOrgs, setSavingOrgs] = useState(false);

  const [password, setPassword] = useState({ new_password: '', confirm_password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  const [flagBusy, setFlagBusy] = useState(false);
  const [confirm, setConfirm] = useState(null);

  const isSelf = user && String(user.id) === String(me?.id);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const [orgs, cat] = await Promise.all([adminUsersApi.organizations(), getRoleCatalog().catch(() => null)]);
        if (cancelled) return;
        setOrganizations(Array.isArray(orgs) ? orgs : []);
        setCatalog(cat);
        const [rows, userOrgs] = await Promise.all([adminUsersApi.list(), adminUsersApi.userOrganizations(id)]);
        if (cancelled) return;
        const found = (rows || []).find((u) => String(u.id) === String(id));
        if (!found) {
          setError('User not found.');
          return;
        }
        setUser(found);
        setProfile({
          first_name: found.first_name || '',
          last_name: found.last_name || '',
          email: found.email || '',
          title: found.title || '',
        });
        setOrgIds((userOrgs || []).map((o) => String(o.id)));
        setOrgRoles(Object.fromEntries((userOrgs || []).map((o) => [String(o.id), o.roles || []])));
      } catch (err) {
        if (!cancelled) setError(apiErrorText(err, 'Failed to load user.'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const validateProfile = () => {
    const errs = {};
    if (!profile.email.trim()) errs.email = 'Email is required';
    else if (!EMAIL_RE.test(profile.email.trim())) errs.email = 'Enter a valid email address';
    if (!profile.first_name.trim()) errs.first_name = 'First name is required';
    if (!profile.last_name.trim()) errs.last_name = 'Last name is required';
    setProfileErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const membershipsPayload = () => orgIds.map((oid) => ({ organization_id: String(oid), roles: orgRoles[oid] || [] }));

  const afterChange = () => organizationsService.clearOrganizationsListCookie();

  const saveProfile = async () => {
    if (!validateProfile()) return;
    setSavingProfile(true);
    try {
      const updated = await adminUsersApi.updateProfile(user.id, {
        first_name: profile.first_name.trim(),
        last_name: profile.last_name.trim(),
        email: profile.email.trim(),
        title: profile.title.trim() || null,
      });
      setUser((prev) => ({ ...prev, ...updated }));
      afterChange();
      toast.success('Profile saved');
    } catch (err) {
      toast.error(apiErrorText(err, 'Failed to save profile.'));
    } finally {
      setSavingProfile(false);
    }
  };

  const saveOrganizations = async () => {
    if (orgIds.length === 0) {
      toast.error('A user must belong to at least one organization.');
      return;
    }
    if (orgIdsMissingRoles(orgIds, orgRoles).length > 0) {
      toast.error('Pick at least one role for every organization.');
      return;
    }
    setSavingOrgs(true);
    try {
      await adminUsersApi.setMemberships(user.id, membershipsPayload());
      afterChange();
      toast.success('Organizations and roles saved');
    } catch (err) {
      toast.error(apiErrorText(err, 'Failed to save organizations.'));
    } finally {
      setSavingOrgs(false);
    }
  };

  const savePassword = async () => {
    if (password.new_password.length < 8) {
      toast.error('Password must be at least 8 characters.');
      return;
    }
    if (password.new_password !== password.confirm_password) {
      toast.error('Passwords do not match.');
      return;
    }
    setSavingPassword(true);
    try {
      await adminUsersApi.setPassword(user.id, password.new_password);
      setPassword({ new_password: '', confirm_password: '' });
      toast.success('Password updated');
    } catch (err) {
      toast.error(apiErrorText(err, 'Failed to set password.'));
    } finally {
      setSavingPassword(false);
    }
  };

  const sendReset = () =>
    setConfirm({
      title: 'Send password reset',
      message: `Email ${user.email} a new temporary password? Their current password will stop working.`,
      confirmText: 'Send',
      run: async () => {
        try {
          await adminUsersApi.sendPasswordReset(user.id);
          toast.success('Password reset email sent');
        } catch (err) {
          toast.error(apiErrorText(err, 'Failed to reset password.'));
        }
      },
    });

  const setFlag = async (fn, patch, okText) => {
    setFlagBusy(true);
    try {
      await fn();
      setUser((prev) => ({ ...prev, ...patch }));
      toast.success(okText);
    } catch (err) {
      toast.error(apiErrorText(err, 'Failed to update.'));
    } finally {
      setFlagBusy(false);
    }
  };

  // Platform grants are deliberate: confirm before changing either flag.
  const confirmFlag = (title, message, run) => setConfirm({ title, message, confirmText: title.split(' ')[0], run });

  const toggleActive = (next) => {
    if (!next) {
      setConfirm({
        title: 'Deactivate user',
        message: `Deactivate ${user.email}? They will no longer be able to sign in and their API keys stop working.`,
        confirmText: 'Deactivate',
        run: () => setFlag(() => adminUsersApi.setActive(user.id, false), { is_active: false }, 'User deactivated'),
      });
      return;
    }
    setFlag(() => adminUsersApi.setActive(user.id, true), { is_active: true }, 'User activated');
  };

  // A newly ticked org starts with no role: the admin must choose one before saving.
  const toggleOrg = (oid, checked) => {
    setOrgIds((prev) => (checked ? [...prev, oid] : prev.filter((x) => x !== oid)));
    if (checked) setOrgRoles((prev) => ({ ...prev, [oid]: prev[oid] || [] }));
  };

  const title = fullName(user) || user?.email || 'User';

  return (
    <div className="space-y-6 pb-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Link to="/admin/users" className="inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-[#18181B]">
            <ArrowLeft className="h-4 w-4" />
            Users
          </Link>
          <h2 className="text-lg font-semibold text-[#18181B]">{title}</h2>
          {user ? <UserAccessTags user={user} catalog={catalog} showStatus /> : null}
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : error ? (
        <p className="text-sm text-red-600">{error}</p>
      ) : (
        <>
        <Card>
            <CardHeader>
              <CardTitle>Password</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="u-pass">New password</Label>
                  <div className="relative">
                    <Input
                      id="u-pass"
                      type={showPassword ? 'text' : 'password'}
                      value={password.new_password}
                      onChange={(e) => setPassword((p) => ({ ...p, new_password: e.target.value }))}
                      className="pr-10"
                      autoComplete="new-password"
                    />
                    <button type="button" onClick={() => setShowPassword((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" aria-label={showPassword ? 'Hide password' : 'Show password'}>
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="u-pass2">Confirm password</Label>
                  <Input id="u-pass2" type={showPassword ? 'text' : 'password'} value={password.confirm_password} onChange={(e) => setPassword((p) => ({ ...p, confirm_password: e.target.value }))} autoComplete="new-password" />
                </div>
              </div>
              <div className="flex flex-wrap justify-end gap-2">
                <Button variant="ghost" onClick={sendReset} disabled={!user.is_active} className="border border-hairline">
                  Email a temporary password
                </Button>
                <Button variant="outline" onClick={savePassword} disabled={savingPassword || !password.new_password} className="gap-1.5">
                  {savingPassword ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Set password
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Profile</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="u-first">First name</Label>
                  <Input id="u-first" value={profile.first_name} onChange={(e) => setProfile((p) => ({ ...p, first_name: sanitizeName(e.target.value) }))} className={profileErrors.first_name ? 'border-red-500' : ''} />
                  {profileErrors.first_name ? <p className="text-xs text-red-600">{profileErrors.first_name}</p> : null}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="u-last">Last name</Label>
                  <Input id="u-last" value={profile.last_name} onChange={(e) => setProfile((p) => ({ ...p, last_name: sanitizeName(e.target.value) }))} className={profileErrors.last_name ? 'border-red-500' : ''} />
                  {profileErrors.last_name ? <p className="text-xs text-red-600">{profileErrors.last_name}</p> : null}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="u-email">Email</Label>
                  <Input id="u-email" type="email" value={profile.email} onChange={(e) => setProfile((p) => ({ ...p, email: sanitizeEmail(e.target.value) }))} className={profileErrors.email ? 'border-red-500' : ''} />
                  {profileErrors.email ? <p className="text-xs text-red-600">{profileErrors.email}</p> : null}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="u-title">Job title</Label>
                  <Input id="u-title" value={profile.title} onChange={(e) => setProfile((p) => ({ ...p, title: sanitizeTitle(e.target.value) }))} placeholder="Optional" />
                </div>
              </div>
              <div className="flex justify-end">
                <Button variant="outline" onClick={saveProfile} disabled={savingProfile} className="gap-1.5">
                  {savingProfile ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Save profile
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Account</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <FlagToggle
                label="Active"
                description="Can sign in and use API keys."
                checked={Boolean(user.is_active)}
                onChange={toggleActive}
                disabled={flagBusy || isSelf}
                disabledReason="You cannot deactivate your own account"
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Platform access</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-xs text-zinc-500">
                Platform-wide grants that sit above organization roles. Changes apply immediately.
              </p>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <FlagToggle
                  label="Full Admin Access"
                  description="Every permission in every organization, plus User Management, Org Management, Bulk Review and the admin test bar."
                  checked={Boolean(user.is_admin)}
                  onChange={(next) => confirmFlag(next ? 'Grant full admin access' : 'Remove full admin access', next ? `Give ${user.email} full admin access to the whole platform?` : `Remove ${user.email}'s full admin access? They keep their organization roles.`, () => setFlag(() => adminUsersApi.setAdmin(user.id, next), { is_admin: next }, `Full admin access ${next ? 'granted' : 'removed'}`))}
                  disabled={flagBusy || isSelf || !user.is_active}
                  disabledReason="You cannot change your own admin status"
                />
                <FlagToggle
                  label="Bulk Contributor"
                  description="May submit bulk uploads to FastCube (approved by an admin) and run FastID reports, on top of org roles."
                  checked={Boolean(user.is_bulk_contributor)}
                  onChange={(next) => confirmFlag(next ? 'Grant bulk contributor' : 'Remove bulk contributor', next ? `Let ${user.email} submit bulk uploads that add consumers to the company-wide database?` : `Remove ${user.email}'s bulk contributor access?`, () => setFlag(() => adminUsersApi.setBulkContributor(user.id, next), { is_bulk_contributor: next }, `Bulk contributor ${next ? 'granted' : 'removed'}`))}
                  disabled={flagBusy || !user.is_active}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Organizations &amp; roles</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {user?.is_admin ? (
                <p className="text-xs text-blue-700">Platform admins hold every permission in every organization; roles below only apply if admin status is removed.</p>
              ) : null}
              <p className="text-xs text-zinc-500">Tick each organization the user belongs to, then pick their roles there. Every organization needs at least one role; without one the user sees nothing in it.</p>
              <OrgRolesPicker
                organizations={organizations}
                catalog={catalog}
                orgIds={orgIds}
                orgRoles={orgRoles}
                onToggleOrg={toggleOrg}
                onRolesChange={(oid, roles) => setOrgRoles((prev) => ({ ...prev, [oid]: roles }))}
                idPrefix="org"
              />
              <div className="flex justify-end">
                <Button variant="outline" onClick={saveOrganizations} disabled={savingOrgs} className="gap-1.5">
                  {savingOrgs ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Save organizations
                </Button>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      <ConfirmDialog
        isOpen={Boolean(confirm)}
        onClose={() => setConfirm(null)}
        onConfirm={() => confirm?.run()}
        title={confirm?.title || 'Confirm'}
        message={confirm?.message || ''}
        confirmText={confirm?.confirmText || 'OK'}
        cancelText="Cancel"
      />
    </div>
  );
}

export default AdminUserDetail;
